import React, { useCallback, use, useEffect, useMemo, useState } from "react";
import { StyleSheet, View } from "react-native";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import ScreenCard from "../ScreenCard";
import { useI18n } from "../../hooks/i18n-hooks";
import TextField from "../../components/TextField";
import VStack from "../../ui_atoms/VStack";
import { useTheme } from "../../hooks/use-theme";
import { Address } from "../../components/address/address";
import AddressField from "../../components/address/AddressField";
import { StackScreenProps } from "@react-navigation/stack";
import Switch from "../../ui_atoms/Switch";
import { AuthManagerContext } from "../../contexts/AuthManager";
import isEmail from "validator/lib/isEmail";
import Text from "../../components/Text";
import { useIntercom } from "../../hooks/intercom";
import MarkdownView from "../../components/MarkdownView";
import { SignupProfileType, useSignup } from "../../hooks/use-signup";
import Gap from "../../ui_atoms/Gap";
import { CREATE_PRIVATE_CLIENT_PROFILE } from "../../graphql/core";
import { useMutation } from "@apollo/client/react";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import { useApolloErrorSnackbar } from "../../hooks/snackbar";
import { LinkSmallTextBold } from "../../components/FormattedText";
import { SignupSigninStackParamList } from "../../navigations/SignupSigninStack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useChainInputFocus } from "../../hooks/use-chain-input-focus";
import { usePreventGoingBack } from "../../hooks/use-prevent-going-back";
import { sleep } from "../../utils";
import { KycStatus } from "../../__generated__/graphql";
import { useAtomValue } from "jotai";
import { currentLang } from "../../atoms/i18n";

type FormData = {
    email: string;
    address: Address;
    terms: boolean;
    firstName?: string;
    lastName?: string;
};

type CheckInfoScreenProps = StackScreenProps<
    SignupSigninStackParamList & RootStackParamList,
    "CheckPrivateClientInfo"
>;

const CheckPrivateClientInfo = ({ navigation, route }: CheckInfoScreenProps) => {
    const i18n = useI18n();
    const styles = useStyles();
    const { setProfile } = use(ProfileSwitcherManagerContext);
    const { user, appExpoPushToken, refreshUser } = use(AuthManagerContext);
    const lang = useAtomValue(currentLang);
    const { signup, loading: signupLoading, error } = useSignup(SignupProfileType.PRIVATE_CLIENT);
    const { isNewProfile = false } = route.params || {};
    const [
        initPrivateClientProfile,
        {
            loading: initPrivateClientProfileLoading,
            error: initPrivateClientProfileError,
            reset: initPrivateClientProfileReset,
        },
    ] = useMutation(CREATE_PRIVATE_CLIENT_PROFILE);

    const [isRefreshingUser, setIsRefreshingUser] = useState(false);

    const loading = signupLoading || initPrivateClientProfileLoading || isRefreshingUser;

    useApolloErrorSnackbar(
        initPrivateClientProfileError,
        initPrivateClientProfileReset,
        i18n.t("screens.auth.check_private_client_info.error"),
    );

    const {
        handleSubmit,
        control,
        formState: { errors, isValid: isFormValid, isDirty },
        reset,
    } = useForm<FormData>({
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: isNewProfile
            ? {
                  ...(user?.workerInfo?.email ? { email: user.workerInfo.email } : {}),
                  ...(user?.workerInfo?.address ? { address: user.workerInfo.address } : {}),
              }
            : {},
    });

    useEffect(() => {
        if (isNewProfile) {
            navigation.setOptions({
                headerTitle: i18n.t("screens.profile_editor.new_profile"),
            });
        }
    }, [i18n, isNewProfile, navigation]);

    const isValid = isFormValid;

    const { presentArticleId, loginUnidentifiedUser } = useIntercom();
    const openMarkdownLink = useCallback(
        (url: string) => {
            void (async () => {
                if (url.startsWith("intercom://")) {
                    const articleId = url.split("intercom://")[1];
                    await loginUnidentifiedUser();
                    await presentArticleId(articleId);
                }
            })();
            return false;
        },
        [loginUnidentifiedUser, presentArticleId],
    );

    const onSubmit = useCallback<SubmitHandler<FormData>>(
        async (data) => {
            const { email, address, firstName, lastName } = data;

            if (isValid) {
                const privateClientUserData = {
                    email,
                    address,
                    expoPushToken: appExpoPushToken,
                    firstName,
                    lastName,
                    lang,
                };
                if (!isNewProfile) {
                    void signup({
                        businessEmail: email,
                        user: privateClientUserData,
                    });
                } else {
                    setIsRefreshingUser(true);
                    try {
                        const { data: initClientProfileData } = await initPrivateClientProfile({
                            variables: {
                                privateClientProfile: {
                                    user: {
                                        email, // be sure we update user private email
                                        address, // and private address
                                    },
                                },
                            },
                            // Skip default refetchQueries — the profile switch
                            // that follows will clearStore() and queries will
                            // refetch with the new profile context.
                            refetchQueries: [],
                        });
                        const currentUser = await refreshUser?.();

                        const newPrivateClientProfile = currentUser?.clientProfiles?.find(
                            (c) => c.id === initClientProfileData?.createPrivateClientProfile.id,
                        );

                        await setProfile(newPrivateClientProfile || null);
                        setIsRefreshingUser(false);
                        reset();
                        // After setIsRefreshingUser(false) + reset(), wait the next render loop before navigating, to avoid the usePreventGoingBack to trigger because of isDirty = true
                        await sleep(0);

                        if (user?.kycStatus?.status !== KycStatus.Accepted) {
                            navigation.navigate("PreIdVerification");
                        } else {
                            navigation.reset({
                                index: 0,
                                routes: [{ name: "Home" }],
                            });
                        }
                    } catch (_e) {
                        // console.error(ex);
                        // this is handled by updateError in useMutation
                    } finally {
                        setIsRefreshingUser(false);
                    }
                }
            }
        },
        [
            appExpoPushToken,
            initPrivateClientProfile,
            isNewProfile,
            isValid,
            navigation,
            refreshUser,
            reset,
            setProfile,
            signup,
            user?.kycStatus?.status,
            lang,
        ],
    );

    usePreventGoingBack({ prevent: loading || isDirty });

    const { chainInputFocus } = useChainInputFocus();

    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.t("submit"),
                    isValid: isValid && !loading,
                    validating: loading,
                    onPress: handleSubmit(onSubmit),
                },
            ]}
            headband={
                error
                    ? {
                          text: i18n.t("screens.auth.check_private_client_info.error"),
                          mode: "error",
                      }
                    : undefined
            }
        >
            <VStack gap={1} h="2/3">
                <>
                    <Text style={styles.introText}>
                        {i18n.t("screens.auth.check_private_client_info.intro")}
                    </Text>
                    <LinkSmallTextBold
                        onPress={() => {
                            void presentArticleId("10938847");
                        }}
                    >
                        {i18n.t("know_more")}
                    </LinkSmallTextBold>
                </>
                <Controller
                    name="email"
                    control={control}
                    rules={{
                        required: true,
                        validate: (value) =>
                            !isEmail(value) ? i18n.t("errors.field_name.email") : undefined,
                    }}
                    render={({ field: { onChange, onBlur, value, name } }) => (
                        <TextField
                            {...chainInputFocus(name)}
                            label={i18n.t("user.email")}
                            value={value}
                            onChangeText={onChange}
                            onBlur={onBlur}
                            keyboardType="email-address"
                            textContentType="emailAddress"
                            autoComplete="email"
                            autoCorrect={false}
                            autoCapitalize="none"
                            error={errors.email?.message}
                            disabled={loading || (isNewProfile && !!user?.workerInfo?.email)}
                        />
                    )}
                />
                <Controller
                    name="address"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, value, name } }) => (
                        <AddressField
                            {...chainInputFocus(name)}
                            label={i18n.t(
                                "screens.auth.check_private_client_info.residential_address",
                            )}
                            value={value}
                            onSelect={onChange}
                            error={!!errors.address}
                            allowedCountries={["ch"]}
                            disabled={loading || (isNewProfile && !!user?.workerInfo?.address)}
                        />
                    )}
                />
                <Controller
                    name="terms"
                    control={control}
                    rules={{
                        required: true,
                    }}
                    render={({ field: { onChange, value } }) => (
                        <View style={styles.termsContainer}>
                            <Switch
                                testID="switch-accept-terms"
                                value={value}
                                onChange={onChange}
                                disabled={loading}
                            />
                            <Gap gap="1/2" />
                            <MarkdownView
                                style={styles.termsMarkdown}
                                markdown={i18n.t(
                                    "screens.auth.check_private_client_info.accept_terms_of_use",
                                )}
                                onLinkPress={openMarkdownLink}
                            />
                        </View>
                    )}
                />
            </VStack>
        </ScreenCard>
    );
};

const useStyles = () => {
    const { fonts, gridSize } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                introText: {
                    ...fonts.bodyMedium,
                    marginTop: gridSize,
                },
                termsContainer: {
                    flexDirection: "row",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                },
                termsText: {
                    ...fonts.bodyMedium,
                    flexShrink: 1,
                },
                termsMarkdown: {
                    /* compensate paragraph margin of markdown */
                    marginTop: -gridSize / 4,
                    flexShrink: 1,
                },
            }),
        [fonts.bodyMedium, gridSize],
    );
};

export default CheckPrivateClientInfo;
